import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { FileText, Folder, Upload, Search, Download, Trash2, FolderPlus, ShieldCheck, KeyRound, Eye, Pencil, DownloadCloud } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement } from '@/hooks/useEntitlement';
import { usePageActions } from '@/components/layout/DashboardActions';
import { useCaseContext } from '../../contexts/CaseContext';
import { encryptFile, decryptToBlob } from '../../lib/zkcrypto';
import { API } from '@/config/api';

const folderColors = ['#3b82f6', '#f97316', '#ec4899', '#10b981', '#8b5cf6'];

export const DocumentsPage = () => {
  const { user } = useAuth();
  // Motor de entitlements: límite de documentos (Demo = 5).
  const { requirePerform } = useEntitlement();
  // Filtro global por expediente activo.
  const { active } = useCaseContext();
  const [expedientes, setExpedientes] = useState([]);
  const [selectedExpId, setSelectedExpId] = useState(active?.expediente_id || '');
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  // Frase de cifrado Zero-Knowledge: vive solo en memoria de sesión, nunca se envía
  const [passphrase, setPassphrase] = useState(() => sessionStorage.getItem('pcl_zk_pass') || '');
  const fileInputRef = useRef(null);

  const savePassphrase = (val) => {
    setPassphrase(val);
    if (val) sessionStorage.setItem('pcl_zk_pass', val);
    else sessionStorage.removeItem('pcl_zk_pass');
  };

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [docsRes, foldersRes, storageRes] = await Promise.all([
        axios.get(`${API}/documents/?lawyer_id=${user.id}`),
        axios.get(`${API}/documents/folders/${user.id}`),
        axios.get(`${API}/integration/storage/${user.id}`),
      ]);
      setDocuments(docsRes.data);
      setFolders(foldersRes.data);
      setStorage(storageRes.data);
    } catch (e) {
      console.error('Error cargando documentos:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Conexión Documentos → Expediente: lista de expedientes para el selector destino.
  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/integration/expedientes?lawyer_id=${user.id}`)
      .then(r => setExpedientes(r.data?.expedientes || []))
      .catch(() => {});
  }, [user?.id]);
  useEffect(() => { if (active?.expediente_id) setSelectedExpId(active.expediente_id); }, [active?.expediente_id]);

  const filtered = documents.filter(d =>
    (d.name || '').toLowerCase().includes(search.toLowerCase()) &&
    (!active?.case_id || d.case_id === active.case_id)
  );

  // ActionBar global → "+ Agregar" dispara la subida de documento.
  usePageActions({ onAdd: () => fileInputRef.current?.click() }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Guardia de cuota de documentos (plan/Demo) antes de cifrar/subir.
    if (!requirePerform('documents', documents.length)) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (!passphrase) {
      alert('Configura tu frase de cifrado antes de subir documentos. Sin ella, nadie (ni el servidor) puede leer tus archivos.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      // Cifrado Zero-Knowledge en el navegador: el servidor solo recibe ciphertext
      const encrypted = await encryptFile(file, passphrase);
      // Destino: expediente seleccionado o el activo del contexto global.
      const target = expedientes.find(e => e.expediente_id === selectedExpId) || (active?.case_id ? active : null);
      await axios.post(`${API}/documents/upload`, {
        lawyer_id: user.id,
        folder: 'Otros',
        expediente_id: target?.expediente_id || null,
        case_id: target?.case_id || null,
        client_id: target?.client_id || null,
        client_name: target?.client_name || null,
        ...encrypted,
      });
      await loadData();
    } catch (err) {
      console.error('Error subiendo documento:', err);
      alert('No se pudo cifrar o subir el documento.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc) => {
    if (!doc.encrypted) {
      alert('Este documento no tiene contenido cifrado descargable.');
      return;
    }
    const pass = passphrase || window.prompt('Ingresa tu frase de cifrado para descifrar el documento:');
    if (!pass) return;
    try {
      const { data } = await axios.get(`${API}/documents/${doc._id}/content`);
      const blob = await decryptToBlob(data, pass);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando documento:', err);
      alert('No se pudo descifrar. ¿La frase es correcta?');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/documents/${id}`);
      setDocuments(prev => prev.filter(d => d._id !== id));
      loadData();
    } catch (err) {
      console.error('Error eliminando documento:', err);
    }
  };

  const handlePreview = async (doc) => {
    if (!doc.encrypted) { alert('Sin contenido cifrado para previsualizar.'); return; }
    const pass = passphrase || window.prompt('Frase de cifrado para descifrar:');
    if (!pass) return;
    try {
      const { data } = await axios.get(`${API}/documents/${doc._id}/content`);
      const blob = await decryptToBlob(data, pass);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert('No se pudo descifrar para vista previa. ¿La frase es correcta?');
    }
  };

  const handleRename = async (doc) => {
    const name = window.prompt('Nuevo nombre del documento:', doc.name);
    if (!name || name === doc.name) return;
    try {
      await axios.patch(`${API}/documents/${doc._id}`, { name });
      loadData();
    } catch (err) { alert('No se pudo renombrar.'); }
  };

  // Almacenamiento descentralizado: vincula un correo de respaldo cuando el cloud
  // del suscriptor (su correo de registro) se llena.
  const linkBackupEmail = async () => {
    const email = window.prompt('Almacenamiento lleno. Vincula un correo de respaldo (o personal) para seguir guardando expedientes:');
    if (!email) return;
    try {
      await axios.post(`${API}/integration/storage/backup-email`, { lawyer_id: user.id, backup_email: email });
      loadData();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo vincular el correo de respaldo. Verifica el formato.');
    }
  };

  const [backingUp, setBackingUp] = useState(false);
  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const { data } = await axios.get(`${API}/backup/manual`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-puntocero-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      alert(data.drive_uploaded
        ? 'Copia de seguridad descargada y subida a Google Drive.'
        : 'Copia de seguridad descargada. (Conecta Google Drive para respaldo automático en la nube.)');
    } catch (err) { alert('No se pudo generar la copia de seguridad.'); }
    finally { setBackingUp(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 pt-12 lg:pt-0">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Documentos</h1>
            <p className="text-white/60">Gestor documental seguro con versionado automático</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleBackup} disabled={backingUp} variant="outline" className="border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10" data-testid="backup-button">
              <DownloadCloud className="w-4 h-4 mr-2" /> {backingUp ? 'Generando...' : 'Copia de seguridad'}
            </Button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} data-testid="file-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-gradient-to-r from-[#f97316] to-[#fb923c] text-white font-bold" data-testid="upload-doc">
              <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Subiendo...' : 'Subir'}
            </Button>
          </div>
        </div>

        {/* Filtro global por expediente activo */}
        {active?.case_id && (
          <div className="rounded-xl border border-[#06b6d4]/30 bg-[#06b6d4]/[0.06] px-4 py-2.5 text-xs text-[#67e8f9]" data-testid="docs-context-filter">
            Mostrando solo documentos del expediente <span className="font-semibold">{active.expediente_id || active.case_number}</span> · {active.client_name}
          </div>
        )}

        {/* Zero-Knowledge passphrase */}
        <div className="backdrop-blur-xl bg-gradient-to-r from-[#10b981]/10 to-[#3b82f6]/10 rounded-2xl p-4 border border-[#10b981]/30">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#10b981]/20 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-5 h-5 text-[#10b981]" />
            </div>
            <div className="flex-1">
              <div className="font-semibold flex items-center gap-2">Cifrado Zero-Knowledge <ShieldCheck className="w-4 h-4 text-[#10b981]" /></div>
              <div className="text-xs text-white/60">Tus archivos se cifran en tu navegador antes de subir a Google Drive. Esta frase nunca se envía al servidor — guárdala bien, sin ella no podrás recuperar tus documentos.</div>
            </div>
            <Input
              type="password"
              value={passphrase}
              onChange={(e) => savePassphrase(e.target.value)}
              placeholder="Tu frase de cifrado"
              className="bg-white/10 border-white/20 text-white md:w-64"
              data-testid="zk-passphrase"
            />
          </div>
        </div>

        {/* Storage Info — almacenamiento descentralizado por suscriptor */}
        <div className="backdrop-blur-xl bg-gradient-to-r from-[#3b82f6]/10 to-[#f97316]/10 rounded-2xl p-4 border border-white/10">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold">Almacenamiento utilizado</span>
            <span className="text-sm text-white/60">{storage ? `${storage.used_human} de ${storage.quota_human}` : '—'}</span>
          </div>
          {storage?.storage_email && (
            <div className="text-[11px] text-white/40 mb-2">
              Cloud vinculado a <span className="text-white/70">{storage.storage_email}</span>
              {storage.backup_email && <> · respaldo: <span className="text-white/70">{storage.backup_email}</span></>}
            </div>
          )}
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#3b82f6] to-[#f97316]" style={{ width: `${Math.min(storage?.percent || 0, 100)}%` }} />
          </div>
        </div>

        {/* Disparador de capacidad: solicita correo de respaldo al llenarse */}
        {storage?.trigger?.needs_backup_email && (
          <div className="backdrop-blur-xl bg-[#ef4444]/10 rounded-2xl p-4 border border-[#ef4444]/40 flex flex-col md:flex-row md:items-center gap-3" data-testid="storage-trigger">
            <div className="w-10 h-10 rounded-xl bg-[#ef4444]/20 flex items-center justify-center flex-shrink-0">
              <DownloadCloud className="w-5 h-5 text-[#ef4444]" />
            </div>
            <div className="flex-1 text-sm text-[#fca5a5]">{storage.trigger.message}</div>
            <Button onClick={linkBackupEmail} className="bg-gradient-to-r from-[#ef4444] to-[#f97316] text-white font-bold flex-shrink-0" data-testid="link-backup-email">
              Vincular correo de respaldo
            </Button>
          </div>
        )}

        {/* Folders */}
        {folders.length > 0 && (
          <div>
            <h2 className="text-xl font-bold mb-4">Carpetas</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {folders.map((f, i) => (
                <motion.div key={f.name} whileHover={{ scale: 1.05 }} className="backdrop-blur-xl bg-white/5 rounded-2xl p-4 border border-white/10 cursor-pointer">
                  <Folder className="w-10 h-10 mb-2" style={{ color: folderColors[i % folderColors.length] }} />
                  <div className="font-semibold text-sm">{f.name}</div>
                  <div className="text-xs text-white/60">{f.count} documentos</div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Expediente destino — todo documento guarda expediente_id / case_id / client_id */}
        <div className="backdrop-blur-xl bg-white/5 rounded-2xl p-4 border border-white/10 flex flex-col md:flex-row md:items-center gap-3">
          <div className="text-sm font-semibold flex items-center gap-2 flex-shrink-0"><Folder className="w-4 h-4 text-[#06b6d4]" /> Expediente destino</div>
          <select value={selectedExpId} onChange={(e) => setSelectedExpId(e.target.value)}
            className="flex-1 px-3 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm" data-testid="doc-expediente-select">
            <option value="" className="bg-[#0f172a]">— Sin expediente (general) —</option>
            {expedientes.map((e) => (
              <option key={e.expediente_id} value={e.expediente_id} className="bg-[#0f172a]">{e.expediente_id} · {e.client_name || e.case_number}</option>
            ))}
          </select>
          <span className="text-[11px] text-white/40">Se guarda expediente_id, case_id y client_id en cada archivo.</span>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar documentos..." className="bg-white/10 border-white/20 text-white pl-10" data-testid="search-doc" />
        </div>

        {/* Documents List */}
        <div className="backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr className="text-left text-xs uppercase text-white/60">
                  <th className="px-4 py-3">Archivo</th>
                  <th className="px-4 py-3 hidden md:table-cell">Cliente</th>
                  <th className="px-4 py-3 hidden md:table-cell">Tamaño</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Fecha</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(doc => (
                  <tr key={doc._id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: doc.type === 'pdf' ? '#ef444420' : '#3b82f620' }}>
                          <FileText className="w-5 h-5" style={{ color: doc.type === 'pdf' ? '#ef4444' : '#3b82f6' }} />
                        </div>
                        <div className="font-medium flex items-center gap-2">
                          {doc.name}
                          {doc.encrypted && <ShieldCheck className="w-3.5 h-3.5 text-[#10b981]" title="Cifrado Zero-Knowledge" />}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm hidden md:table-cell">{doc.client}</td>
                    <td className="px-4 py-3 text-sm hidden md:table-cell">{doc.size}</td>
                    <td className="px-4 py-3 text-sm hidden lg:table-cell">{doc.date}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handlePreview(doc)} className="p-1.5 rounded-lg hover:bg-white/10" title="Vista previa" data-testid={`preview-doc-${doc._id}`}><Eye className="w-4 h-4 text-white/70" /></button>
                        <button onClick={() => handleRename(doc)} className="p-1.5 rounded-lg hover:bg-white/10" title="Editar / renombrar" data-testid={`edit-doc-${doc._id}`}><Pencil className="w-4 h-4 text-[#3b82f6]" /></button>
                        <button onClick={() => handleDownload(doc)} className="p-1.5 rounded-lg hover:bg-white/10" title="Descargar" data-testid={`download-doc-${doc._id}`}><Download className="w-4 h-4 text-[#10b981]" /></button>
                        <button onClick={() => handleDelete(doc._id)} className="p-1.5 rounded-lg hover:bg-white/10" title="Eliminar" data-testid={`delete-doc-${doc._id}`}><Trash2 className="w-4 h-4 text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loading && <div className="text-center py-8 text-white/50">Cargando documentos...</div>}
            {!loading && filtered.length === 0 && <div className="text-center py-8 text-white/50">No hay documentos. Sube tu primer archivo.</div>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
